import React, {
    useEffect, useState, useRef, useContext,
} from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import PropTypes from 'prop-types';
import _ from 'lodash';
import { TextField, Paper, IconButton } from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import { useParams } from 'react-router-dom';
import { MainContext } from '../../App/AdminLayout';
import languageObject from '../../../translations/messages';
import GridContainer from '../../../components/Grid/GridContainer';
import GridItem from '../../../components/Grid/GridItem';
import Button from '../../../components/Button/CustomButton';
import Snackbar from '../../../components/Snackbar/Snackbar';
import { AddBoxIcon, IndeterminateCheckBoxIcon } from '../../../components/Icons/Icon';
import WrapperContainer from '../../../components/Container/WrapperContainer';
import TimeRanger from '../../../components/DatePicker/TimeRanger';
import Loader from '../../../components/Loader/Loader';
import { APIErrors } from '../../../common/Constants';
import Validation from '../../../common/Validation';
import RoomData from './RoomData';
import TimeData from './TimeData';
import styles from '../../../assets/jss/pages/SettingSeminarRoomStyle';
import DateRangerHasNull from '../../../components/DatePicker/DateRangerHasNull';

const useStyles = makeStyles(styles);
const gridColWidth = {
    first: 3,
    second: 9,
};
const subgridColWidth = {
    first: 9,
    second: 3,
};

const SettingSeminarRoomPage = ({
    settingSeminarRoomData, errors, isCompleted, getSettingSeminarRoom, updateSettingSeminarRoom, getSettingSeminarRoomClear,
    updateSettingSeminarRoomClear, locale, isLoading, eventInfo,
}) => {
    const classes = useStyles();
    const [, forceUpdate] = useState();
    const [dialog, setDialog] = useState({ isOpen: false, type: 'success', message: '' });
    const [validateResults, setValidateResults] = useState({});
    const submitData = useRef([new RoomData({
        sdate: eventInfo.sdate,
        edate: eventInfo.edate,
    })]);
    const mainPanelRef = useContext(MainContext); // Used to scroll to top after validation
    const { shortName } = useParams();

    useEffect(() => {
        if (shortName) getSettingSeminarRoom(shortName);
        return () => { if (shortName) getSettingSeminarRoomClear(); };
    }, [getSettingSeminarRoom, getSettingSeminarRoomClear, shortName]);

    useEffect(() => {
        if (settingSeminarRoomData.length) {
            submitData.current = _.cloneDeep(settingSeminarRoomData);
            forceUpdate({});
        }
    }, [settingSeminarRoomData]);

    useEffect(() => {
        if (isCompleted && errors.code !== APIErrors.badRequest) {
            setDialog({
                isOpen: true,
                type: errors.message ? 'error' : 'success',
                message: errors.message || languageObject[locale]['apiSuccess.default'],
            });
            updateSettingSeminarRoomClear();
        }
        if (isCompleted && errors.code === APIErrors.badRequest && errors.non_field_errors) {
            setDialog({
                isOpen: true,
                type: 'error',
                message: errors.non_field_errors.message,
            });
            updateSettingSeminarRoomClear();
        }
    }, [isCompleted, errors, updateSettingSeminarRoomClear, locale]);

    const handleCloseErrorDialog = (e, reason) => {
        if (reason === 'clickaway') return;
        setDialog({ ...dialog, isOpen: false });
    };

    const handleClickApply = () => {
        const isValid = validate(submitData.current);
        if (isValid) updateSettingSeminarRoom(shortName, submitData.current);
    };

    const handleAddRoom = () => {
        submitData.current.push(new RoomData({
            id: submitData.current.length ? submitData.current[submitData.current.length - 1].id + 1 : 0,
            sdate: eventInfo.sdate,
            edate: eventInfo.edate,
        }));
        forceUpdate({});
    };

    const handleRemoveRoom = (id) => {
        if (submitData.current.length <= 1) return;
        submitData.current = submitData.current.filter((room) => room.id !== id);
        forceUpdate({});
    };

    const handleAddTime = (id) => {
        for (let i = 0; i < submitData.current.length; i += 1) {
            if (submitData.current[i].id === id) {
                const newTimes = submitData.current[i].times;
                newTimes.push(new TimeData({
                    id: newTimes.length ? newTimes[newTimes.length - 1].id + 1 : 0,
                }));
                break;
            }
        }
        forceUpdate({});
    };

    const handleRemoveTime = (id, timeID) => {
        for (let i = 0; i < submitData.current.length; i += 1) {
            if (submitData.current[i].id === id) {
                const newTimes = submitData.current[i].times;
                if (newTimes.length <= 1) return;
                submitData.current[i].times = newTimes.filter((time) => time.id !== timeID);
                break;
            }
        }
        forceUpdate({});
    };

    const handleChangeName = (id, e) => {
        for (let i = 0; i < submitData.current.length; i += 1) {
            if (submitData.current[i].id === id) {
                submitData.current[i].room_name = e.target.value;
                break;
            }
        }
        forceUpdate({});
    };
    const handleChangeNameEN = (id, e) => {
        for (let i = 0; i < submitData.current.length; i += 1) {
            if (submitData.current[i].id === id) {
                submitData.current[i].room_name_en = e.target.value;
                break;
            }
        }
        forceUpdate({});
    };
    const handleChangeSDate = (id, e) => {
        for (let i = 0; i < submitData.current.length; i += 1) {
            if (submitData.current[i].id === id) {
                submitData.current[i].sdate = e;
                break;
            }
        }
        forceUpdate({});
    };

    const handleChangeEDate = (id, e) => {
        for (let i = 0; i < submitData.current.length; i += 1) {
            if (submitData.current[i].id === id) {
                submitData.current[i].edate = e;
                break;
            }
        }
        forceUpdate({});
    };

    const handleChangePrice = (id, e) => {
        for (let i = 0; i < submitData.current.length; i += 1) {
            if (submitData.current[i].id === id && /^\d*$/.test(e.target.value)) {
                submitData.current[i].price = e.target.value;
                break;
            }
        }
        forceUpdate({});
    };

    const handleChangeTime = (id, timeID, timeField, value) => {
        for (let i = 0; i < submitData.current.length; i += 1) {
            if (submitData.current[i].id === id) {
                const newTimes = submitData.current[i].times;
                for (let j = 0; j < newTimes.length; j += 1) {
                    if (newTimes[j].id === timeID) {
                        newTimes[j][timeField] = value;
                    }
                }
                break;
            }
        }
    };

    const validate = (values) => {
        const validateRules = {};
        for (let i = 0; i < values.length; i += 1) {
            validateRules[`${values[i].id}_room_name`] = [
                () => Validation.isNotEmpty(values[i].room_name),
                () => Validation.validateMaxLength(values[i].room_name, 30),
            ];
            validateRules[`${values[i].id}_room_name_en`] = [
                () => !_.isEmpty(eventInfo.languages) && Validation.isNotEmpty(values[i].room_name_en),
                () => !_.isEmpty(eventInfo.languages) && Validation.validateLatin(values[i].room_name_en),
                () => !_.isEmpty(eventInfo.languages) && Validation.validateMaxLength(values[i].room_name_en, 30),
            ];
            validateRules[`${values[i].id}_sdate`] = [
                () => Validation.isNotEmpty(values[i].sdate),
            ];
            validateRules[`${values[i].id}_edate`] = [
                () => Validation.isNotEmpty(values[i].edate),
            ];
            validateRules[`${values[i].id}_price`] = [
                () => Validation.isNotEmpty(values[i].price),
            ];
            validateRules[`${values[i].id}_ranger_date`] = [
                () => Validation.validateDateRanger(values[i].sdate, values[i].edate),
            ];
            const { times } = values[i];
            for (let j = 0; j < times.length; j += 1) {
                addTimeValidationRules(validateRules, `${values[i].id}_${times[j].id}`, times[j].stime, times[j].etime);
            }
        }

        const validateResult = Validation.validateAllRules(validateRules);

        if (Object.keys(validateResult).length > 0) {
            setValidateResults({ ...validateResult });
            mainPanelRef.current.scrollTo(0, 0);
            return false;
        }
        setValidateResults({});
        return true;
    };

    const addTimeValidationRules = (validateRules, prefix, stime, etime) => {
        validateRules[`${prefix}_stime`] = [
            () => Validation.isNotEmpty(stime),
            () => Validation.validateTimeFormat(stime),
        ];
        validateRules[`${prefix}_etime`] = [
            () => Validation.isNotEmpty(etime),
            () => Validation.validateTimeFormat(etime),
        ];
        validateRules[`${prefix}_rangeTime`] = [
            () => Validation.validateTimeRanger(stime, etime),
        ];
    };
    return (
        <>
            <WrapperContainer>
                {submitData.current.map((room) => (
                    <GridItem key={room.id}>
                        <SettingSeminarRoom
                            {...room}
                            handleRemoveRoom={handleRemoveRoom}
                            handleAddTime={handleAddTime}
                            handleRemoveTime={handleRemoveTime}
                            handleChangeName={handleChangeName}
                            handleChangeNameEN={handleChangeNameEN}
                            handleChangeSDate={handleChangeSDate}
                            handleChangeEDate={handleChangeEDate}
                            handleChangePrice={handleChangePrice}
                            handleChangeTime={handleChangeTime}
                            validateRoomResults={validateResults[room.id]}
                            validateResults={validateResults}
                            eventInfo={eventInfo}
                        />
                    </GridItem>
                ))}
                <GridItem>
                    <IconButton disabled={!eventInfo.has_seminar} size="small" classes={{ root: classes.roomAddButton }} onClick={handleAddRoom}>
                        <AddBoxIcon size="40px" />
                    </IconButton>
                </GridItem>
                <GridItem>
                    <GridItem md={12} style={{ textAlign: 'center', marginTop: 20 }}>
                        <Button
                            color="info"
                            style={{ width: '200px', fontSize: '20px' }}
                            size="md"
                            gradient
                            onClick={handleClickApply}
                            disabled={!eventInfo.has_seminar}
                        >
                            <FormattedMessage id="apply" />
                        </Button>
                    </GridItem>
                </GridItem>
            </WrapperContainer>
            <Snackbar
                open={dialog.isOpen}
                onClose={handleCloseErrorDialog}
                message={dialog.message}
                severity={dialog.type}
            />
            <Loader isLoading={isLoading} />
        </>
    );
};

const SettingSeminarRoom = ({
    id, times, room_name, room_name_en, handleRemoveRoom, handleAddTime, handleRemoveTime, handleChangeName, handleChangeNameEN, handleChangeTime,
    validateResults, sdate, edate, price, handleChangeSDate, handleChangeEDate, handleChangePrice, eventInfo,
}) => {
    const classes = useStyles();
    const intl = useIntl();

    const removeRoom = () => handleRemoveRoom && handleRemoveRoom(id);
    const addTime = () => handleAddTime && handleAddTime(id);
    const changeName = (e) => handleChangeName && handleChangeName(id, e);
    const changeNameEN = (e) => handleChangeNameEN && handleChangeNameEN(id, e);
    const changeSDate = (e) => handleChangeSDate && handleChangeSDate(id, e);
    const changeEDate = (e) => handleChangeEDate && handleChangeEDate(id, e);
    const changePrice = (e) => handleChangePrice && handleChangePrice(id, e);

    const roomNameMessageID = _.get(validateResults, `${id}_room_name.message`);
    const roomNameMessageENID = _.get(validateResults, `${id}_room_name_en.message`);
    const priceMessageID = _.get(validateResults, `${id}_price.message`);
    const sdateMessageID = _.get(validateResults, `${id}_sdate.message`);
    const edateMessageID = _.get(validateResults, `${id}_edate.message`);
    const rangerMessageID = _.get(validateResults, `${id}_ranger_date.message`);

    return (
        <Paper elevation={3} classes={{ root: classes.room }}>
            <IconButton size="small" classes={{ root: classes.roomRemoveButton }} onClick={removeRoom}>
                <IndeterminateCheckBoxIcon size="40px" />
            </IconButton>
            <GridContainer alignItems="center" spacing={2}>
                <GridItem md={gridColWidth.first}>
                    <FormattedMessage id="adminSettingSeminarRoom.roomName" />
                </GridItem>
                <GridItem md={gridColWidth.second}>
                    <TextField
                        variant="outlined"
                        inputProps={{ maxLength: 30 }}
                        value={room_name}
                        className={classes.name}
                        onChange={changeName}
                        error={!!_.get(validateResults, `${id}_room_name`)}
                        helperText={roomNameMessageID ? intl.formatMessage({ id: roomNameMessageID }, { maxLength: 30 }) : ''}
                    />
                </GridItem>
                {!_.isEmpty(eventInfo.languages) && (
                    <GridItem md={gridColWidth.first}>
                        <FormattedMessage id="adminSettingSeminarRoom.roomNameEN" />
                    </GridItem>
                )}
                {!_.isEmpty(eventInfo.languages) && (
                    <GridItem md={gridColWidth.second}>
                        <TextField
                            variant="outlined"
                            inputProps={{ maxLength: 30 }}
                            value={room_name_en}
                            className={classes.name}
                            onChange={changeNameEN}
                            error={!!_.get(validateResults, `${id}_room_name_en`)}
                            helperText={roomNameMessageENID ? intl.formatMessage({ id: roomNameMessageENID }, { maxLength: 30 }) : ''}
                        />
                    </GridItem>
                )}
                <GridItem md={gridColWidth.first}>
                    開催期間
                </GridItem>
                <GridItem md={gridColWidth.second}>
                    <DateRangerHasNull
                        defaultStartDate={sdate || null}
                        defaultEndDate={edate || null}
                        onChangeStartDate={changeSDate}
                        onChangeEndDate={changeEDate}
                        error={{
                            startDate: !!_.get(validateResults, `${id}_sdate`),
                            endDate: !!_.get(validateResults, `${id}_edate`),
                            rangerDate: !!_.get(validateResults, `${id}_ranger_date`),
                        }}
                        helperTexts={{
                            startDate: sdateMessageID
                                ? intl.formatMessage({ id: sdateMessageID }) : '',
                            endDate: edateMessageID
                                ? intl.formatMessage({ id: edateMessageID }) : '',
                            rangerDate: rangerMessageID
                                ? intl.formatMessage({ id: rangerMessageID }) : '',
                        }}
                        maxDate={eventInfo.edate}
                        minDate={eventInfo.sdate}
                    />
                </GridItem>

                <GridItem md={gridColWidth.first}>
                    料金
                </GridItem>
                <GridItem md={gridColWidth.second}>
                    <TextField
                        variant="outlined"
                        value={price}
                        className={classes.name}
                        onChange={changePrice}
                        error={!!_.get(validateResults, `${id}_price`)}
                        helperText={priceMessageID ? intl.formatMessage({ id: priceMessageID }) : ''}
                        inputProps={{
                            min: 0,
                        }}
                        InputProps={{ classes: { root: `${classes.numberInput} ${classes.price}` } }}
                    />
                </GridItem>

                {times.map((time, index) => (
                    <SettingSeminarTime
                        key={time.id}
                        index={index}
                        roomID={id}
                        {...time}
                        handleRemoveTime={handleRemoveTime}
                        handleChangeTime={handleChangeTime}
                        validateResults={validateResults}
                    />
                ))}

                <GridItem md={gridColWidth.first} />
                <GridItem md={gridColWidth.second}>
                    <GridContainer alignItems="center">
                        <GridItem md={subgridColWidth.first} />
                        <GridItem md={subgridColWidth.second}>
                            <IconButton size="small" classes={{ root: classes.timeAddButton }} onClick={addTime}>
                                <AddBoxIcon />
                            </IconButton>
                        </GridItem>
                    </GridContainer>
                </GridItem>
            </GridContainer>
        </Paper>
    );
};

const SettingSeminarTime = ({
    roomID, id, stime, etime, handleRemoveTime, handleChangeTime, index, validateResults,
}) => {
    const classes = useStyles();
    const intl = useIntl();

    const removeTime = () => handleRemoveTime && handleRemoveTime(roomID, id);
    const changeStartTime = (value) => handleChangeTime && handleChangeTime(roomID, id, 'stime', value);
    const changeEndTime = (value) => handleChangeTime && handleChangeTime(roomID, id, 'etime', value);

    const stimeMessageID = _.get(validateResults, `${roomID}_${id}_stime.message`);
    const etimeMessageID = _.get(validateResults, `${roomID}_${id}_etime.message`);
    const rangeTimeMessageID = _.get(validateResults, `${roomID}_${id}_rangeTime.message`);

    const defaultStartTime = stime instanceof Date && Number.isNaN(stime.getTime()) ? '00:00' : stime;
    const defaultEndTime = etime instanceof Date && Number.isNaN(etime.getTime()) ? '00:00' : etime;

    return (
        <>
            <GridItem md={gridColWidth.first}>
                <FormattedMessage id="timeRange" />
                {` ${index + 1}`}
            </GridItem>
            <GridItem md={gridColWidth.second}>
                <GridContainer alignItems="center">
                    <GridItem md={subgridColWidth.first} className={classes.timeWrapper}>
                        <TimeRanger
                            defaultStartTime={defaultStartTime}
                            defaultEndTime={defaultEndTime}
                            onChangeStartTime={changeStartTime}
                            onChangeEndTime={changeEndTime}
                            error={{
                                startTime: !!_.get(validateResults, `${roomID}_${id}_stime`),
                                endTime: !!_.get(validateResults, `${roomID}_${id}_etime`),
                                rangerTime: !!_.get(validateResults, `${roomID}_${id}_rangeTime`),
                            }}
                            helperTexts={{
                                startTime: stimeMessageID ? intl.formatMessage({ id: stimeMessageID }) : '',
                                endTime: etimeMessageID ? intl.formatMessage({ id: etimeMessageID }) : '',
                                rangerTime: rangeTimeMessageID ? intl.formatMessage({ id: rangeTimeMessageID }) : '',
                            }}
                        />
                    </GridItem>
                    <GridItem md={subgridColWidth.second}>
                        <IconButton size="small" classes={{ root: classes.timeRemoveButton }} onClick={removeTime}>
                            <IndeterminateCheckBoxIcon />
                        </IconButton>
                    </GridItem>
                </GridContainer>
            </GridItem>
        </>
    );
};

SettingSeminarTime.defaultProps = {
    stime: '',
    etime: '',
    roomID: -1,
    id: -1,
    handleRemoveTime: undefined,
    handleChangeTime: undefined,
    index: -1,
    validateResults: {},
};

SettingSeminarTime.propTypes = {
    stime: PropTypes.string,
    etime: PropTypes.string,
    roomID: PropTypes.number,
    id: PropTypes.number,
    handleRemoveTime: PropTypes.func,
    handleChangeTime: PropTypes.func,
    index: PropTypes.number,
    validateResults: PropTypes.objectOf(PropTypes.object),
};

SettingSeminarRoom.defaultProps = {
    times: [],
    room_name: '',
    room_name_en: '',
    sdate: '',
    edate: '',
    price: 0,
    id: -1,
    handleRemoveRoom: undefined,
    handleAddTime: undefined,
    handleRemoveTime: undefined,
    handleChangeName: undefined,
    handleChangeNameEN: undefined,
    handleChangeTime: undefined,
    handleChangeSDate: undefined,
    handleChangeEDate: undefined,
    handleChangePrice: undefined,
    validateResults: {},
    eventInfo: {},
};

SettingSeminarRoom.propTypes = {
    times: PropTypes.arrayOf(PropTypes.object),
    room_name: PropTypes.string,
    room_name_en: PropTypes.string,
    sdate: PropTypes.string,
    edate: PropTypes.string,
    price: PropTypes.number,
    id: PropTypes.number,
    handleRemoveRoom: PropTypes.func,
    handleAddTime: PropTypes.func,
    handleRemoveTime: PropTypes.func,
    handleChangeName: PropTypes.func,
    handleChangeNameEN: PropTypes.func,
    handleChangeTime: PropTypes.func,
    handleChangePrice: PropTypes.func,
    handleChangeEDate: PropTypes.func,
    handleChangeSDate: PropTypes.func,
    validateResults: PropTypes.objectOf(PropTypes.object),
    eventInfo: PropTypes.objectOf(PropTypes.any),
};

SettingSeminarRoomPage.defaultProps = {
    settingSeminarRoomData: {},
    errors: {},
    eventInfo: {},
    getSettingSeminarRoom: undefined,
    updateSettingSeminarRoom: undefined,
    getSettingSeminarRoomClear: undefined,
    updateSettingSeminarRoomClear: undefined,
};

SettingSeminarRoomPage.propTypes = {
    settingSeminarRoomData: PropTypes.arrayOf(PropTypes.oneOfType([
        PropTypes.array,
        PropTypes.object,
    ])),
    errors: PropTypes.objectOf(PropTypes.any),
    eventInfo: PropTypes.objectOf(PropTypes.any),
    isCompleted: PropTypes.bool.isRequired,
    getSettingSeminarRoom: PropTypes.func,
    updateSettingSeminarRoom: PropTypes.func,
    getSettingSeminarRoomClear: PropTypes.func,
    updateSettingSeminarRoomClear: PropTypes.func,
    locale: PropTypes.string.isRequired,
    isLoading: PropTypes.bool.isRequired,
};

export default SettingSeminarRoomPage;
